import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FaMoneyBillWave, FaChartLine, FaRegCheckCircle, FaSlidersH, FaTools, FaPlus } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL;

interface StatementRow {
  id: number;
  date_str: string;
  particulars: string;
  cheque_no?: string;
  debit: number;
  credit: number;
  balance: number;
  remarks?: string;
}

interface MonthlySummary {
  monthKey: string; // e.g. "2026-05"
  monthName: string; // e.g. "May 2026"
  totalDebits: number;
  recurringDebits: number;
  variableDebits: number;
  transactionsCount: number;
}

const SurvivalPrediction: React.FC = () => {
  const [statementRows, setStatementRows] = useState<StatementRow[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Custom recurring keywords
  const [keywords, setKeywords] = useState<string[]>([
    'salary', 'rent', 'electricity', 'internet', 'aws', 'server', 'cloud', 
    'telecom', 'subscription', 'emi', 'maintenance', 'insurance', 'hosting'
  ]);
  const [newKeyword, setNewKeyword] = useState('');

  // Manual exclusions/inclusions by particular ID or text
  const [userExclusions, setUserExclusions] = useState<string[]>([]);
  const [userInclusions, setUserInclusions] = useState<string[]>([]);

  // Discretionary spending survival retention factor (default 20%)
  const [variableRetention, setVariableRetention] = useState<number>(20);

  useEffect(() => {
    fetchStatementRows();
  }, []);

  const fetchStatementRows = () => {
    setLoading(true);
    axios.get(`${API_URL}/auth/bank-statement-rows`)
      .then((res) => {
        setStatementRows(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => toast.error('Failed to load bank statement records.'))
      .finally(() => setLoading(false));
  };

  const parseRowDate = (dateStr: string | null): Date => {
    if (!dateStr) return new Date();
    const clean = dateStr.trim();
    const parts = clean.split(/[-/\s]+/);
    if (parts.length === 3) {
      const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
      let monthVal = NaN;
      if (/^\d+$/.test(parts[1])) {
        monthVal = Number(parts[1]) - 1;
      } else {
        monthVal = months.indexOf(parts[1].toLowerCase());
      }
      
      let year = NaN;
      let day = NaN;
      if (parts[0].length === 4) {
        year = Number(parts[0]);
        day = Number(parts[2]);
      } else {
        year = Number(parts[2]);
        day = Number(parts[0]);
      }

      if (!isNaN(year) && !isNaN(monthVal) && monthVal >= 0 && !isNaN(day)) {
        return new Date(year, monthVal, day);
      }
    }
    const d = new Date(clean);
    return isNaN(d.getTime()) ? new Date() : d;
  };

  // Process statement rows into monthly summaries
  const monthlyData = useMemo(() => {
    const isRecurring = (particulars: string): boolean => {
      const text = particulars.toLowerCase();
      if (userExclusions.includes(particulars)) return false;
      if (userInclusions.includes(particulars)) return true;
      return keywords.some(kw => text.includes(kw.toLowerCase()));
    };

    const monthsMap = new Map<string, { totalDebits: number; recurringDebits: number; transactions: StatementRow[] }>();

    statementRows.forEach((row) => {
      const debitVal = Number(row.debit || 0);
      if (debitVal <= 0) return; // Only expenses

      const dateObj = parseRowDate(row.date_str);
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const monthKey = `${year}-${month}`;

      let current = monthsMap.get(monthKey);
      if (!current) {
        current = { totalDebits: 0, recurringDebits: 0, transactions: [] };
        monthsMap.set(monthKey, current);
      }

      current.totalDebits += debitVal;
      if (isRecurring(row.particulars)) {
        current.recurringDebits += debitVal;
      }
      current.transactions.push(row);
    });

    const list: MonthlySummary[] = [];
    const formatter = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });

    monthsMap.forEach((val, key) => {
      const [y, m] = key.split('-');
      const date = new Date(Number(y), Number(m) - 1, 1);
      list.push({
        monthKey: key,
        monthName: formatter.format(date),
        totalDebits: val.totalDebits,
        recurringDebits: val.recurringDebits,
        variableDebits: Math.max(0, val.totalDebits - val.recurringDebits),
        transactionsCount: val.transactions.length
      });
    });

    // Sort chronologically (oldest first for trend analysis)
    return list.sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  }, [statementRows, keywords, userExclusions, userInclusions]);

  // Overall Statistics
  const stats = useMemo(() => {
    if (monthlyData.length === 0) {
      return { avgDebits: 0, avgRecurring: 0, avgVariable: 0 };
    }
    const totalDebitsSum = monthlyData.reduce((s, m) => s + m.totalDebits, 0);
    const totalRecurringSum = monthlyData.reduce((s, m) => s + m.recurringDebits, 0);
    const totalVariableSum = monthlyData.reduce((s, m) => s + m.variableDebits, 0);
    const count = monthlyData.length;

    return {
      avgDebits: totalDebitsSum / count,
      avgRecurring: totalRecurringSum / count,
      avgVariable: totalVariableSum / count
    };
  }, [monthlyData]);

  // Unique list of debit transaction particulars for customization
  const uniqueParticulars = useMemo(() => {
    const set = new Set<string>();
    statementRows.forEach(row => {
      if (Number(row.debit || 0) > 0 && row.particulars) {
        set.add(row.particulars.trim());
      }
    });
    return Array.from(set).sort();
  }, [statementRows]);

  // Predicted survival amount
  const predictedSurvival = useMemo(() => {
    return stats.avgRecurring + (stats.avgVariable * (variableRetention / 100));
  }, [stats, variableRetention]);

  const addKeyword = () => {
    if (!newKeyword.trim()) return;
    if (keywords.includes(newKeyword.trim().toLowerCase())) {
      toast.error('Keyword already exists.');
      return;
    }
    setKeywords([...keywords, newKeyword.trim().toLowerCase()]);
    setNewKeyword('');
    toast.success('Keyword added.');
  };

  const removeKeyword = (kw: string) => {
    setKeywords(keywords.filter(k => k !== kw));
  };

  const toggleManualClassification = (particular: string) => {
    if (userInclusions.includes(particular)) {
      // Switch to excluded
      setUserInclusions(userInclusions.filter(p => p !== particular));
      setUserExclusions([...userExclusions, particular]);
    } else if (userExclusions.includes(particular)) {
      // Reset to default keyword matching
      setUserExclusions(userExclusions.filter(p => p !== particular));
    } else {
      // Set to included
      const text = particular.toLowerCase();
      const originallyRecurring = keywords.some(kw => text.includes(kw));
      if (originallyRecurring) {
        setUserExclusions([...userExclusions, particular]);
      } else {
        setUserInclusions([...userInclusions, particular]);
      }
    }
  };

  const getParticularStatus = (particular: string) => {
    if (userInclusions.includes(particular)) return 'recurring-override';
    if (userExclusions.includes(particular)) return 'variable-override';
    const text = particular.toLowerCase();
    const isMatched = keywords.some(kw => text.includes(kw));
    return isMatched ? 'recurring-auto' : 'variable';
  };

  return (
    <div className="space-y-8 animate__animated animate__fadeIn">
      {/* Overview Intro */}
      <div className="border-b border-blue-50 pb-4">
        <h3 className="text-xl font-bold text-blue-900">Monthly Survival Budget & Expense Forecast</h3>
        <p className="text-sm text-gray-500 mt-1">
          Analyses debit transactions from uploaded statements, auto-detects monthly recurring expenses (burn rate), and calculates the predicted monthly cash required to keep operations running.
        </p>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-500 font-medium">Loading statement debits...</div>
      ) : statementRows.length === 0 ? (
        <div className="py-12 bg-blue-50/30 border border-dashed border-blue-200 rounded-3xl text-center text-gray-500">
          <p className="font-semibold text-lg text-blue-950 mb-2">No Statement Data Found</p>
          <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">You need to upload bank statements first so the system can evaluate historic operating expenses.</p>
          <a href="/statement-upload" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-5 py-2.5 rounded-xl shadow-md transition">
            Upload Statement
          </a>
        </div>
      ) : (
        <>
          {/* KPI Cards Panel */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-lg flex flex-col justify-between relative overflow-hidden hover:shadow-xl transition duration-255">
              <div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">Avg Monthly Expense</span>
                <h4 className="text-lg sm:text-xl lg:text-2xl font-black text-slate-800 mt-1 truncate" title={`₹${stats.avgDebits.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}>
                  ₹{stats.avgDebits.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </h4>
              </div>
              <p className="text-[10px] text-gray-500 mt-4">Total historic monthly debits average</p>
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-lg flex flex-col justify-between relative overflow-hidden hover:shadow-xl transition duration-255">
              <div>
                <span className="text-xs font-bold text-blue-500 uppercase tracking-wide">Fixed Recurring Expense</span>
                <h4 className="text-lg sm:text-xl lg:text-2xl font-black text-blue-700 mt-1 truncate" title={`₹${stats.avgRecurring.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}>
                  ₹{stats.avgRecurring.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </h4>
              </div>
              <p className="text-[10px] text-gray-500 mt-4">Core overhead burn (rent, salaries, etc.)</p>
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-lg flex flex-col justify-between relative overflow-hidden hover:shadow-xl transition duration-255">
              <div>
                <span className="text-xs font-bold text-orange-500 uppercase tracking-wide">Variable Expenses</span>
                <h4 className="text-lg sm:text-xl lg:text-2xl font-black text-orange-600 mt-1 truncate" title={`₹${stats.avgVariable.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}>
                  ₹{stats.avgVariable.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </h4>
              </div>
              <p className="text-[10px] text-gray-500 mt-4">Flexible & discretionary spending</p>
            </div>

            <div className="bg-gradient-to-br from-indigo-900 to-blue-800 text-white rounded-2xl p-5 shadow-xl flex flex-col justify-between relative overflow-hidden hover:shadow-2xl transition duration-255">
              <div className="absolute right-[-10px] top-[-10px] opacity-10 text-white">
                <FaMoneyBillWave size={120} />
              </div>
              <div>
                <span className="text-xs font-bold text-indigo-200 uppercase tracking-wide">Survival Prediction Target</span>
                <h4 className="text-xl sm:text-2xl lg:text-3xl font-black text-white mt-1 truncate" title={`₹${predictedSurvival.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}>
                  ₹{predictedSurvival.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </h4>
              </div>
              <p className="text-[10px] text-indigo-100 mt-4">Fixed Burn + {variableRetention}% of Variable Expenses</p>
            </div>
          </div>

          {/* Interactive Simulation Controls */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100/50 rounded-3xl p-6 shadow-md">
            <h4 className="font-extrabold text-blue-900 text-md mb-2 flex items-center gap-2">
              <FaSlidersH className="text-blue-600 animate-pulse" /> Discretionary Expense Reduction Simulator
            </h4>
            <p className="text-xs text-gray-600 mb-4">
              To predict the minimum amount required to survive, you can simulate cutting down discretionary (variable) spending.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <input
                type="range"
                min="0"
                max="100"
                value={variableRetention}
                onChange={(e) => setVariableRetention(Number(e.target.value))}
                className="w-full sm:flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="bg-white border rounded-xl px-4 py-2 text-center shadow-sm shrink-0 min-w-[120px]">
                <div className="text-[10px] font-bold text-gray-400 uppercase">Variable Retained</div>
                <div className="text-lg font-black text-blue-800">{variableRetention}%</div>
              </div>
              <div className="bg-white border rounded-xl px-4 py-2 text-center shadow-sm shrink-0 min-w-[120px]">
                <div className="text-[10px] font-bold text-gray-400 uppercase">Variable Cut</div>
                <div className="text-lg font-black text-red-600">{100 - variableRetention}%</div>
              </div>
            </div>
          </div>

          {/* Trend Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Historical Trend Table */}
            <div className="lg:col-span-2 bg-white border border-blue-50 rounded-2xl p-5 shadow-lg">
              <h4 className="font-extrabold text-blue-950 text-sm mb-4 flex items-center gap-2">
                <FaChartLine className="text-blue-600" /> Historical Monthly Operating Expenses
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100 text-slate-500 uppercase font-bold tracking-wider">
                      <th className="p-3">Month</th>
                      <th className="p-3 text-right">Fixed Recurring (A)</th>
                      <th className="p-3 text-right">Variable Overhead (B)</th>
                      <th className="p-3 text-right">Total Debit Burn (A+B)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    {monthlyData.map((m) => (
                      <tr key={m.monthKey} className="hover:bg-slate-50/50">
                        <td className="p-3 font-semibold text-slate-900">{m.monthName}</td>
                        <td className="p-3 text-right text-blue-700 font-medium">
                          ₹{m.recurringDebits.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </td>
                        <td className="p-3 text-right text-orange-600 font-medium">
                          ₹{m.variableDebits.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </td>
                        <td className="p-3 text-right font-bold text-slate-900">
                          ₹{m.totalDebits.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-slate-50 font-bold border-t border-slate-200 text-slate-900">
                      <td className="p-3">Historical Average</td>
                      <td className="p-3 text-right text-blue-800">
                        ₹{stats.avgRecurring.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="p-3 text-right text-orange-700">
                        ₹{stats.avgVariable.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="p-3 text-right text-slate-950">
                        ₹{stats.avgDebits.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Keyword Manager */}
            <div className="bg-white border border-blue-50 rounded-2xl p-5 shadow-lg flex flex-col justify-between">
              <div>
                <h4 className="font-extrabold text-blue-950 text-sm mb-2 flex items-center gap-2">
                  <FaTools className="text-indigo-600" /> Recurring Keywords Rules
                </h4>
                <p className="text-[11px] text-gray-500 mb-4">
                  Any debit with particulars containing these matching words will be automatically grouped as Fixed Recurring Expenses.
                </p>

                {/* Input Add */}
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    placeholder="e.g. salary, rent..."
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    className="flex-1 text-xs border border-gray-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-blue-500"
                  />
                  <button
                    onClick={addKeyword}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg p-2 transition flex items-center justify-center"
                  >
                    <FaPlus size={10} />
                  </button>
                </div>

                {/* Keywords List */}
                <div className="flex flex-wrap gap-1.5 max-h-[160px] overflow-y-auto pr-1">
                  {keywords.map((kw) => (
                    <span key={kw} className="inline-flex items-center gap-1 bg-blue-50 text-blue-900 border border-blue-100 px-2 py-0.5 rounded-full text-[10px] font-semibold">
                      {kw}
                      <button
                        onClick={() => removeKeyword(kw)}
                        className="text-red-500 hover:text-red-700 font-bold ml-0.5"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4 mt-4 text-[10px] text-gray-400">
                You can add or remove rules to customize how your survival core burn rate behaves.
              </div>
            </div>
          </div>

          {/* Detailed Transactions List & Override Manager */}
          <div className="bg-white border border-blue-50 rounded-2xl p-5 shadow-lg">
            <h4 className="font-extrabold text-blue-950 text-sm mb-2 flex items-center gap-2">
              <FaRegCheckCircle className="text-emerald-600" /> Transaction Particulars Categorization Manager
            </h4>
            <p className="text-xs text-gray-500 mb-4">
              Below are the unique particulars from your debit entries. Click to override them as **Fixed/Recurring** or **Variable/One-off**.
            </p>

            <div className="max-h-[300px] overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-50 text-xs">
              {uniqueParticulars.map((part) => {
                const status = getParticularStatus(part);
                return (
                  <div key={part} className="flex justify-between items-center p-2.5 hover:bg-slate-50">
                    <span className="font-medium text-slate-800 max-w-[70%] truncate" title={part}>
                      {part}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                        status === 'recurring-auto' ? 'bg-blue-100 text-blue-800' :
                        status === 'recurring-override' ? 'bg-emerald-100 text-emerald-800' :
                        status === 'variable-override' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {status === 'recurring-auto' ? 'Recurring (Auto)' :
                         status === 'recurring-override' ? 'Recurring (User)' :
                         status === 'variable-override' ? 'Variable (User)' :
                         'Variable'}
                      </span>
                      <button
                        onClick={() => toggleManualClassification(part)}
                        className="text-[10px] text-blue-600 hover:text-blue-800 font-bold border border-blue-100 px-2 py-0.5 rounded-lg hover:bg-blue-50 transition"
                      >
                        Toggle Type
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SurvivalPrediction;
